import React, { useState, useEffect } from 'react';
import { Plus, Trash2, Save, XCircle, GraduationCap, Building2, Loader2 } from 'lucide-react';
import api from '../../utils/axios';

const CollegeManager = () => {
  const [colleges, setColleges] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    type: 'College',
    programs: [''],
    isActive: true,
  });

  useEffect(() => {
    fetchColleges();
  }, []);

  const fetchColleges = async () => {
    try {
      setLoading(true);
      const res = await api.get('/colleges');
      setColleges(res.data.data);
    } catch (err) {
      console.error('Failed to fetch colleges', err);
    } finally {
      setLoading(false);
    }
  };

  const handleAddProgram = () => {
    setFormData({ ...formData, programs: [...formData.programs, ''] });
  };

  const handleRemoveProgram = (idx) => {
    const updated = formData.programs.filter((_, i) => i !== idx);
    setFormData({ ...formData, programs: updated.length ? updated : [''] });
  };

  const handleProgramChange = (idx, value) => {
    const updated = [...formData.programs];
    updated[idx] = value;
    setFormData({ ...formData, programs: updated });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const cleanData = {
        ...formData,
        programs: formData.programs.filter(p => p.trim() !== ''),
      };

      if (editingId) {
        await api.put(`/colleges/${editingId}`, cleanData);
      } else {
        await api.post('/colleges', cleanData);
      }

      setShowForm(false);
      setEditingId(null);
      setFormData({ name: '', type: 'College', programs: [''], isActive: true });
      fetchColleges();
    } catch (err) {
      alert('Failed to save college');
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (college) => {
    setFormData({
      name: college.name,
      type: college.type,
      programs: college.programs.length > 0 ? college.programs : [''],
      isActive: college.isActive,
    });
    setEditingId(college._id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this college?')) {
      try {
        await api.delete(`/colleges/${id}`);
        setColleges(colleges.filter(c => c._id !== id));
      } catch (err) {
        alert('Failed to delete');
      }
    }
  };

  const handleToggleActive = async (college) => {
    try {
      await api.put(`/colleges/${college._id}`, { isActive: !college.isActive });
      fetchColleges();
    } catch (err) {
      alert('Failed to update status');
    }
  };

  const cancelForm = () => {
    setShowForm(false);
    setEditingId(null);
    setFormData({ name: '', type: 'College', programs: [''], isActive: true });
  };

  return (
    <div className="space-y-8">
      {/* Header Banner */}
      <div className="bg-gradient-to-r from-[#003366] to-[#004488] text-white p-8 rounded-sm relative overflow-hidden">
        <div className="absolute top-0 right-0 w-1/3 h-full bg-white/5 -skew-x-12 translate-x-10"></div>
        <div className="relative z-10 flex justify-between items-center">
          <div>
            <h4 className="text-2xl font-black uppercase tracking-tighter mb-2 italic flex items-center">
              <GraduationCap size={28} className="mr-3" /> College & School Manager
            </h4>
            <p className="text-[10px] font-bold uppercase tracking-widest text-white/70 max-w-xl">
              Add colleges, schools, and their programs. These will appear in the Admission Inquiry form dropdown.
            </p>
          </div>
          <button
            onClick={() => { cancelForm(); setShowForm(true); }}
            className="bg-[#cc0000] text-white px-6 py-3 rounded-sm font-black uppercase text-xs tracking-widest flex items-center hover:bg-white hover:text-[#cc0000] transition-all shadow-xl"
          >
            <Plus className="w-4 h-4 mr-2" /> Add College
          </button>
        </div>
      </div>

      {/* Add/Edit Form */}
      {showForm && (
        <div className="bg-white border border-slate-200 rounded-sm shadow-sm p-8 animate-scaleIn">
          <div className="flex justify-between items-center mb-8">
            <h3 className="text-lg font-black uppercase tracking-tighter text-slate-900">
              {editingId ? 'Edit College / School' : 'Add New College / School'}
            </h3>
            <button onClick={cancelForm} className="text-slate-400 hover:text-red-500">
              <XCircle size={22} />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">College / School Name</label>
                <input
                  type="text"
                  required
                  placeholder="e.g. Arya Institute of Engineering & Technology"
                  className="w-full border-b-2 border-slate-100 py-3 outline-none focus:border-[#cc0000] font-bold text-sm"
                  value={formData.name}
                  onChange={e => setFormData({ ...formData, name: e.target.value })}
                />
              </div>
              <div>
                <label className="block text-[10px] font-black uppercase text-slate-500 mb-2">Institution Type</label>
                <select
                  className="w-full border-b-2 border-slate-100 py-3 outline-none focus:border-[#cc0000] font-bold text-sm bg-transparent"
                  value={formData.type}
                  onChange={e => setFormData({ ...formData, type: e.target.value })}
                >
                  <option value="College">College</option>
                  <option value="School">School</option>
                  <option value="Institute">Institute</option>
                  <option value="University">University</option>
                </select>
              </div>
            </div>

            {/* Programs */}
            <div>
              <label className="block text-[10px] font-black uppercase text-slate-500 mb-3">
                Programs / Courses Offered
              </label>
              <div className="space-y-3">
                {formData.programs.map((program, idx) => (
                  <div key={idx} className="flex items-center gap-3 group">
                    <input
                      type="text"
                      placeholder={`e.g. B.Tech (Computer Science)`}
                      className="flex-1 border border-slate-100 bg-slate-50 px-4 py-3 rounded-sm outline-none focus:border-[#cc0000] text-sm font-semibold"
                      value={program}
                      onChange={e => handleProgramChange(idx, e.target.value)}
                    />
                    {formData.programs.length > 1 && (
                      <button
                        type="button"
                        onClick={() => handleRemoveProgram(idx)}
                        className="text-red-400 hover:text-red-600 opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        <XCircle size={18} />
                      </button>
                    )}
                  </div>
                ))}
                <button
                  type="button"
                  onClick={handleAddProgram}
                  className="flex items-center text-[10px] font-black uppercase text-[#003366] hover:text-[#cc0000] pt-1"
                >
                  <Plus size={14} className="mr-1" /> Add Another Program
                </button>
              </div>
            </div>

            {/* Active Toggle */}
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                id="isActive"
                checked={formData.isActive}
                onChange={e => setFormData({ ...formData, isActive: e.target.checked })}
                className="w-4 h-4 accent-[#cc0000]"
              />
              <label htmlFor="isActive" className="text-sm font-bold text-slate-700">Active (visible in inquiry form)</label>
            </div>

            <div className="flex gap-4 pt-4">
              <button
                type="submit"
                disabled={saving}
                className="bg-[#cc0000] text-white px-10 py-4 font-black uppercase text-xs tracking-[3px] shadow-xl hover:bg-slate-900 transition-all disabled:opacity-50 flex items-center"
              >
                {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Saving...</> : <><Save className="w-4 h-4 mr-2" /> {editingId ? 'Update' : 'Save'} College</>}
              </button>
              <button
                type="button"
                onClick={cancelForm}
                className="bg-slate-100 text-slate-600 px-8 py-4 font-black uppercase text-xs tracking-widest hover:bg-slate-200 transition-all"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* College List */}
      <div className="bg-white border border-slate-200 rounded-sm shadow-sm">
        <div className="p-6 border-b border-slate-100 flex justify-between items-center">
          <h3 className="text-sm font-black uppercase tracking-widest text-slate-600">
            All Colleges & Schools ({colleges.length})
          </h3>
        </div>

        {loading ? (
          <div className="p-20 text-center">
            <Loader2 size={32} className="animate-spin text-slate-300 mx-auto" />
            <p className="text-slate-400 font-bold text-xs mt-4 uppercase tracking-widest">Loading colleges...</p>
          </div>
        ) : colleges.length === 0 ? (
          <div className="p-20 text-center">
            <Building2 size={48} className="text-slate-200 mx-auto mb-4" />
            <p className="text-slate-400 font-bold text-sm">No colleges added yet</p>
            <p className="text-slate-300 text-xs mt-1">Click "Add College" to get started</p>
          </div>
        ) : (
          <div className="divide-y divide-slate-50">
            {colleges.map((college) => (
              <div key={college._id} className="p-6 flex items-start justify-between hover:bg-slate-50/50 transition-colors group">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="text-lg font-black text-slate-900">{college.name}</h4>
                    <span className={`px-3 py-0.5 text-[9px] font-black uppercase tracking-widest rounded-full ${
                      college.type === 'College' ? 'bg-blue-50 text-blue-600' :
                      college.type === 'School' ? 'bg-green-50 text-green-600' :
                      college.type === 'Institute' ? 'bg-purple-50 text-purple-600' :
                      'bg-amber-50 text-amber-600'
                    }`}>
                      {college.type}
                    </span>
                    <span className={`px-3 py-0.5 text-[9px] font-black uppercase tracking-widest rounded-full ${
                      college.isActive ? 'bg-emerald-50 text-emerald-600' : 'bg-red-50 text-red-500'
                    }`}>
                      {college.isActive ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  {college.programs && college.programs.length > 0 && (
                    <div className="flex flex-wrap gap-2 mt-3">
                      {college.programs.map((prog, i) => (
                        <span key={i} className="bg-slate-100 text-slate-600 px-3 py-1 text-[11px] font-bold rounded-sm">
                          {prog}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
                  <button
                    onClick={() => handleToggleActive(college)}
                    className={`px-3 py-2 rounded-sm text-[10px] font-black uppercase tracking-wider transition-all ${
                      college.isActive 
                        ? 'bg-amber-50 text-amber-600 hover:bg-amber-100' 
                        : 'bg-emerald-50 text-emerald-600 hover:bg-emerald-100'
                    }`}
                  >
                    {college.isActive ? 'Deactivate' : 'Activate'}
                  </button>
                  <button
                    onClick={() => handleEdit(college)}
                    className="bg-slate-100 text-slate-600 px-3 py-2 rounded-sm text-[10px] font-black uppercase tracking-wider hover:bg-[#003366] hover:text-white transition-all"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleDelete(college._id)}
                    className="bg-red-50 text-red-500 px-3 py-2 rounded-sm hover:bg-red-500 hover:text-white transition-all"
                  >
                    <Trash2 size={14} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default CollegeManager;
